// Seeded routes and forecasts (spec §9). Deterministic: mulberry32 seeded by seed × domain salt.
import type { Domain, Route, AirSegment, SeaSegment, AirEnv, SeaEnv } from '../types';
import { AIR_LAYERS_FT } from '../types';

export const ROUTE_LENGTH_NM = 30000;
export const AIR_SEGMENT_NM = 100;
export const SEA_SEGMENT_NM = 50;
/** Extra distance for diverting around a weather cell (spec §9). */
export const DIVERT_NM = 40;
/** Push-through penalties (spec §9): +15 % drag in the air, +30 % resistance at sea. */
export const AIR_PUSH_PENALTY = 0.15;
export const SEA_PUSH_PENALTY = 0.3;
/** estimate: wind amplitude per AIR_LAYERS_FT layer (kt); the top layer is the ±60 kt jet-stream band of spec §9. */
export const WIND_AMP_KT = [15, 25, 35, 50, 60] as const;
/** estimate: share of segments carrying a weather cell. */
const WEATHER_CELL_PROBABILITY = 0.08;
const DOMAIN_SALT: Record<Domain, number> = { air: 0x1b873593, sea: 0xcc9e2d51 };

/** mulberry32 — a small, fast 32-bit PRNG. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** UTC calendar date as YYYYMMDD. */
export function todaySeed(now: Date = new Date()): number {
  return now.getUTCFullYear() * 10000 + (now.getUTCMonth() + 1) * 100 + now.getUTCDate();
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const round1 = (v: number): number => Math.round(v * 10) / 10;

/** Bounded random walk: each segment moves by up to ±30 % of the amplitude. */
function walk(rand: () => number, prev: number, lo: number, hi: number): number {
  return clamp(prev + (rand() * 2 - 1) * (hi - lo) * 0.15, lo, hi);
}

export function generateRoute(seed: number, domain: Domain, lengthNm: number = ROUTE_LENGTH_NM): Route {
  const rand = mulberry32(Math.imul(seed, DOMAIN_SALT[domain]) >>> 0);
  if (domain === 'air') {
    const segments: AirSegment[] = [];
    let winds = WIND_AMP_KT.map((amp) => (rand() * 2 - 1) * amp);
    for (let from = 0; from < lengthNm; from += AIR_SEGMENT_NM) {
      winds = winds.map((w, i) => walk(rand, w, -WIND_AMP_KT[i], WIND_AMP_KT[i]));
      const seg: AirSegment = { fromNm: from, toNm: Math.min(from + AIR_SEGMENT_NM, lengthNm), windKtByLayer: winds.map(round1) };
      if (from >= 2 * AIR_SEGMENT_NM && rand() < WEATHER_CELL_PROBABILITY) seg.weatherCell = { divertNm: DIVERT_NM, pushPenalty: AIR_PUSH_PENALTY };
      segments.push(seg);
    }
    return { seed, domain, segments, lengthNm };
  }
  const segments: SeaSegment[] = [];
  let current = rand() * 5 - 2; // −2 … +3 kn (spec §9)
  let sea = Math.floor(rand() * 4); // start calm to moderate
  for (let from = 0; from < lengthNm; from += SEA_SEGMENT_NM) {
    current = walk(rand, current, -2, 3);
    const r = rand();
    if (r < 0.15) sea = clamp(sea + 1, 0, 6);
    else if (r < 0.3) sea = clamp(sea - 1, 0, 6);
    const seg: SeaSegment = { fromNm: from, toNm: Math.min(from + SEA_SEGMENT_NM, lengthNm), currentKn: round1(current), seaState: sea };
    if (from >= 2 * SEA_SEGMENT_NM && rand() < WEATHER_CELL_PROBABILITY) seg.weatherCell = { divertNm: DIVERT_NM, pushPenalty: SEA_PUSH_PENALTY };
    segments.push(seg);
  }
  return { seed, domain, segments, lengthNm };
}

export function isAirRoute(route: Route): route is Route & { segments: AirSegment[] } {
  return route.domain === 'air';
}

/** Segment index at a route position; beyond the end the last segment's conditions persist. */
export function segmentIndexAt(route: Route, positionNm: number): number {
  const n = route.segments.length;
  if (n === 0) return 0;
  const len = route.domain === 'air' ? AIR_SEGMENT_NM : SEA_SEGMENT_NM;
  return clamp(Math.floor(Math.max(0, positionNm) / len), 0, n - 1);
}

/** Along-track wind at an altitude, linear between the forecast layers. */
export function windAtKt(seg: AirSegment, altFt: number): number {
  const layers = AIR_LAYERS_FT;
  if (altFt <= layers[0]) return seg.windKtByLayer[0];
  for (let i = 1; i < layers.length; i++) {
    if (altFt <= layers[i]) {
      const t = (altFt - layers[i - 1]) / (layers[i] - layers[i - 1]);
      return seg.windKtByLayer[i - 1] + t * (seg.windKtByLayer[i] - seg.windKtByLayer[i - 1]);
    }
  }
  return seg.windKtByLayer[layers.length - 1];
}

export function airEnvFor(seg: AirSegment, dragPenalty: number): AirEnv {
  return { windAlongKt: (altFt) => windAtKt(seg, altFt), dragPenalty };
}

export function seaEnvFor(seg: SeaSegment, resistancePenalty: number): SeaEnv {
  return { currentKn: seg.currentKn, seaState: seg.seaState, resistancePenalty };
}
